import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ContentDataPDFType } from '../../types';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import styles from './content-pdf.module.scss';
import './content-pdf.global.scss';
import DownloadIcon from '../../assets/icons/download.svg?react';
import PrintIcon from '../../assets/icons/print.svg?react';
import Spinner from '../spinner/Spinner';
import { downloadFile } from '../../services/File';
import Button from "../button/Button";

// Import the getMediaAbsolutePath function from File.ts instead of defining it here
import { getMediaAbsolutePath } from '../../services/File';
import { logger } from '../../utils/logger';
import {
  configurePdfJsMainThreadFallback,
  configurePdfJsWorker,
  hasConfiguredPdfJsMainThreadFallback,
} from '../../utils/configurePdfJsWorker';
import { getPdfViewerErrorMessage } from '../../utils/pdfViewerCompatibility';

type Props = {
  data: ContentDataPDFType;
  className?: string;
};

configurePdfJsWorker();

const roundScale = (num: number) => Math.round(num * 100) / 100;

function ContentPDF(props: Props) {
  const {data, className = ''} = props;
  const [totalPages, setTotalPages] = useState(0);
  const [pageWidth, setPageWidth] = useState<number | undefined>(undefined);
  const [scale, setScale] = useState(0.8);
  const [downloading, setDownloading] = useState(false);
  const [PDFViewerOneError, setPDFViewerOneError] = useState(false);
  const [pdfLoadError, setPdfLoadError] = useState<unknown>(null);
  const [documentKey, setDocumentKey] = useState(0);

  const isTablet = useMemo(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    return /(ipad|tablet|(android(?!.*mobile))|(windows(?!.*phone)(.*touch))|kindle|playbook|silk|(puffin(?!.*(IP|AP|WP))))/.test(userAgent)
  }, []);

  const isPhone = useMemo(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    const regexs = [/(Android)(.+)(Mobile)/i, /BlackBerry/i, /iPhone|iPod/i, /Opera Mini/i, /IEMobile/i]
    return regexs.some((b) => userAgent.match(b))
  }, [])
  useEffect(() => {
    if (isTablet || isPhone) {
      setScale(1); 
    }
  }, [isTablet, isPhone])
  const increaseScale = useCallback(() => {
    const newScale = Math.min(scale + 0.1, 2);
    setScale(roundScale(newScale));
  }, [scale]);

  const decreaseScale = useCallback(() => {
    const newScale = Math.max(scale - 0.1, 0.1);
    setScale(roundScale(newScale));
  }, [scale]);

  const listener = useCallback(() => {
    setTimeout(() => {
      const dpfClient = document.getElementById('pdfWrapper');
      if (dpfClient) {
        setPageWidth(dpfClient.clientWidth - 30);
      } else {
        setPageWidth(window.innerWidth - 30);
      }
    }, 100);
  }, []);

  useEffect(() => {
    window.addEventListener('resize', listener);

    listener();
    return () => {
      window.removeEventListener('resize', listener);
    };
  }, [listener])

  const [delayedFileObj, setDelayedFileObj] = useState<string | null>(null);
  
  // Add a delay before loading the PDF to ensure it's fully propagated to the CDN
  useEffect(() => {
    if (data) {
      // Clear any existing file object first
      setDelayedFileObj(null);
      
      // Set a delay before loading the PDF - longer for larger files
      // This helps ensure the file is fully propagated to the CDN
      const timer = setTimeout(() => {
        const path = getMediaAbsolutePath(data);
        logger.debug('[PDF Viewer] Loading PDF from:', path);
        
        // Pre-fetch the PDF to check its size
        fetch(path, { method: 'HEAD' })
          .then(response => {
            const contentLength = response.headers.get('Content-Length');
            logger.debug('[PDF Viewer] PDF size:', contentLength ? `${Math.round(parseInt(contentLength) / 1024 / 1024)}MB` : 'unknown');
            
            // Set the file object to trigger loading
            setDelayedFileObj(path);
          })
          .catch(error => {
            logger.error('[PDF Viewer] Error pre-fetching PDF:', error);
            // Still try to load the PDF even if pre-fetch fails
            setDelayedFileObj(path);
          });
      }, 2000); // Increased delay to 2 seconds
      
      return () => clearTimeout(timer);
    }
  }, [data]);
  
  const fileObj = delayedFileObj;


  const onDocumentLoadSuccess = useCallback(({numPages}) => {
    logger.info(`[PDF Viewer] PDF loaded successfully with ${numPages} pages`);
    setTotalPages(numPages);
  }, []);

  const pages = useMemo(() => {
    if (fileObj && totalPages && pageWidth) {
      return Array.from(
        new Array(totalPages),
        (el, index) => {
          return (
            <div key={`page_${index + 1}`}>
              <Page
                width={pageWidth}
                key={`page_${index + 1}`}
                pageNumber={index + 1}
                loading={''}
                scale={scale}
              />
              {index < totalPages - 1 && <hr className={styles.pageDivider}/>}
            </div>
          )
        },
      );
    }

  }, [fileObj, totalPages, pageWidth, scale]);

  const download = useCallback(() => {
    if (data) {
      setDownloading(true);
      downloadFile(data, 'pdf')
        .catch(() => {
          alert(`Can't download file.`);
        })
        .finally(() => {
          setDownloading(false);
        });
    }
  }, [data]);


  useEffect(() => {
    if (delayedFileObj) {
      logger.debug('[PDF Viewer] Fetching PDF for print functionality');
      
      // Use a timeout to avoid blocking the main thread
      const fetchTimer = setTimeout(() => {
        fetch(delayedFileObj)
          .then(resp => {
            if (!resp.ok) {
              throw new Error(`HTTP error! Status: ${resp.status}`);
            }
            return resp.blob();
          })
          .then(blob => {
            logger.debug('[PDF Viewer] PDF fetched successfully, size:', `${Math.round(blob.size / 1024 / 1024)}MB`);
            const newPDFBlob = new Blob([blob], {type: 'application/pdf'});
            const localUrl = window.URL.createObjectURL(newPDFBlob);
            const iframeEle = document.getElementById("hiddenFileHackForPrint");
            if (iframeEle) {
              // @ts-ignore
              iframeEle.src = localUrl;
            }
            window.URL.revokeObjectURL(localUrl);
          }).catch((e) => {
            logger.error('[PDF Viewer] Error fetching PDF:', e);
          });
      }, 0);
      
      return () => clearTimeout(fetchTimer);
    }
  }, [delayedFileObj])

  const printFile = () => {
    let getPrintEle = document.getElementById("hiddenFileHackForPrint");

    setTimeout(() => {
      if (getPrintEle?.getAttribute("src") === null) {
        throw new Error('PDF not loaded');
      }
      (getPrintEle as HTMLIFrameElement)?.contentWindow?.print();
    }, 500)
  }

  if (!fileObj) return null;

  return (
    <div id="pdfWrapper" className={[styles.wrapper, className].join(' ')}>
      <iframe id="hiddenFileHackForPrint" title="hiddenFileHackForPrint" style={{display: "none"}}/>
      {!isPhone && <div className={styles.toolbar}>
        <button disabled={downloading} onClick={download} className={styles.toolbarBtn}>
          <DownloadIcon/>
        </button>
        <button className={styles.toolbarBtn} onClick={printFile}><PrintIcon/></button>
        {!PDFViewerOneError && <>
         <button className={styles.toolbarBtn} onClick={decreaseScale}>-</button>
         <button className={styles.toolbarBtn} onClick={increaseScale}>+</button>
         <div className={styles.scaleTitle}>{Number(scale * 100).toFixed(0)}%</div>
        </>}
      </div>}
      {!PDFViewerOneError ?
        <div className={styles.content}>
          <Document
            key={documentKey}
            onLoadError={(error) => {
              logger.error('[PDF Viewer] Error loading PDF:', error);
              if (!hasConfiguredPdfJsMainThreadFallback()) {
                void configurePdfJsMainThreadFallback().then(() => {
                  setDocumentKey((key) => key + 1);
                });
                return;
              }
              setPdfLoadError(error);
              setPDFViewerOneError(true);
            }}
            file={fileObj}
            onLoadSuccess={onDocumentLoadSuccess}
            className={styles.document}
            loading={
              <div className={styles.loadingContainer}>
                <Spinner/>
                <div className={styles.loadingText}>Loading PDF...</div>
                <div className={styles.loadingSubtext}>Large files may take longer to load</div>
              </div>
            }
            externalLinkTarget={'_blank'}
          >
            {pages}
          </Document>
        </div> :
        <div className={styles.contentTwo}>
          <div style={{padding: '20px', textAlign: 'center'}}>
            <div style={{marginBottom: 12, fontWeight: 600}}>
              {getPdfViewerErrorMessage(pdfLoadError).title}
            </div>
            <div style={{marginBottom: 20}}>
              {getPdfViewerErrorMessage(pdfLoadError).body} You can also download the file:
            </div>
            <Button
              className={styles.emergencyButton}
              label={"Download your PDF"}
              onClick={download}/>
          </div>
        </div>
      }
    </div>
  );
}

export default ContentPDF;
